import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { globalSearch, type SearchGroup } from "@/services/searchService";

/**
 * Recherche de l'en-tête : élèves, parents, classes, enseignants, personnel, utilisateurs (ou écoles pour
 * le super-administrateur), selon les droits. Un clic ouvre la page concernée déjà filtrée.
 */
export default function HeaderSearch() {
  const navigate = useNavigate();
  const { can, isPlatformAdmin } = useAuth();
  const [term, setTerm] = useState("");
  const [groups, setGroups] = useState<SearchGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  /** Téléphone : la recherche s'ouvre en barre pleine largeur au-dessus de l'en-tête. */
  const [mobile, setMobile] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  const results = groups.flatMap((g) => g.results);

  // Recherche lancée 300 ms après la dernière frappe ; une réponse dépassée est ignorée.
  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) {
      setGroups([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(() => {
      globalSearch(q, can, isPlatformAdmin)
        .then((found) => {
          if (cancelled) return;
          setGroups(found);
          setActive(0);
        })
        .catch(() => !cancelled && setGroups([]))
        .finally(() => !cancelled && setLoading(false));
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [term, can, isPlatformAdmin]);

  // Clic en dehors : la liste se ferme.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (boxRef.current?.contains(target) || mobileRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (link: string) => {
    setOpen(false);
    setMobile(false);
    setTerm("");
    navigate(link);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      setMobile(false);
      return;
    }
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active].link);
    }
  };

  const showPanel = open && term.trim().length >= 2;
  let index = -1;

  const placeholder = isPlatformAdmin ? "Rechercher une école..." : "Rechercher un élève, un parent, une classe...";
  const panel = (
    <>
      {!loading && groups.length === 0 && (
        <p className="px-3 py-4 text-center text-sm text-foreground-500 break-words">Aucun résultat pour « {term.trim()} ».</p>
      )}
      {groups.map((group) => (
        <div key={group.title} className="mb-1 last:mb-0">
          <p className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-foreground-400">{group.title}</p>
          {group.results.map((r) => {
            index += 1;
            const current = index;
            return (
              <button
                key={r.key}
                type="button"
                onMouseEnter={() => setActive(current)}
                onClick={() => go(r.link)}
                className={`flex w-full min-h-[2.75rem] items-center gap-3 rounded-md px-3 py-2 text-left cursor-pointer active:bg-background-200 ${
                  current === active ? "bg-background-100" : ""
                }`}
              >
                <span className="w-8 h-8 shrink-0 rounded-md bg-primary-50 text-primary-700 flex items-center justify-center" aria-hidden="true">
                  <i className={`${r.icon} text-sm`}></i>
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm text-foreground-900">{r.label}</span>
                  <span className="block truncate text-xs text-foreground-500">{r.detail}</span>
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </>
  );
  const input = (autoFocus: boolean) => (
    <input
      value={term}
      autoFocus={autoFocus}
      onChange={(e) => {
        setTerm(e.target.value);
        setOpen(true);
      }}
      onFocus={() => setOpen(true)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      aria-label="Recherche"
      enterKeyHint="search"
      autoComplete="off"
      className="min-w-0 flex-1 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
    />
  );

  return (
    <>
      {/* Téléphone : une loupe ; la recherche s'ouvre en pleine largeur. */}
      <button
        type="button"
        onClick={() => {
          setMobile(true);
          setOpen(true);
        }}
        className="press md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
        aria-label="Rechercher"
      >
        <i className="ri-search-line text-xl"></i>
      </button>
      {mobile && (
        <div
          ref={mobileRef}
          className="fixed inset-x-0 top-0 z-[60] border-b border-background-200 bg-background-50 px-3 pb-2 pt-[calc(0.5rem+env(safe-area-inset-top,0px))] shadow-pop animate-fade-in md:hidden"
        >
          <div className="flex items-center gap-2">
            <div className="flex min-h-[2.75rem] flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 focus-within:border-primary-500">
              <i className={loading ? "ri-loader-4-line animate-spin text-foreground-400" : "ri-search-line text-foreground-400"} aria-hidden="true"></i>
              {input(true)}
            </div>
            <button
              type="button"
              onClick={() => {
                setMobile(false);
                setOpen(false);
              }}
              className="press min-h-[2.75rem] px-2 text-sm font-medium text-primary-700 cursor-pointer"
            >
              Fermer
            </button>
          </div>
          {showPanel && <div className="mt-2 max-h-[70dvh] overflow-y-auto overscroll-contain">{panel}</div>}
        </div>
      )}

      <div ref={boxRef} className="relative hidden md:block">
        <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2 transition-[border-color,box-shadow] duration-150 focus-within:border-primary-500 focus-within:ring-[3px] focus-within:ring-primary-100">
          <span className="w-4 h-4 flex items-center justify-center text-foreground-400" aria-hidden="true">
            <i className={loading ? "ri-loader-4-line animate-spin text-base" : "ri-search-line text-base"}></i>
          </span>
          <span className="flex w-48 xl:w-72">{input(false)}</span>
          {term && (
            <button
              type="button"
              onClick={() => setTerm("")}
              className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
              aria-label="Effacer la recherche"
            >
              <i className="ri-close-line"></i>
            </button>
          )}
        </div>

        {showPanel && (
          <div className="absolute left-0 mt-2 w-[26rem] max-h-[70vh] origin-top-left overflow-y-auto overscroll-contain animate-scale-in rounded-lg border border-background-200 bg-background-50 p-2 shadow-pop">
            {panel}
          </div>
        )}
      </div>
    </>
  );
}
